import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Play, History, User as UserIcon, LogOut, ChevronRight, Swords, Plus } from 'lucide-react';
import Logo from '../components/Logo';
import api from '../api/client';
import { useSocket } from '../hooks/useSocket';
import ConfirmModal from '../components/ConfirmModal';

const DashboardPage: React.FC = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [games, setGames] = useState<any[]>([]);
    const [challenges, setChallenges] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [copySuccess, setCopySuccess] = useState<string | null>(null);
    const [gameToDelete, setGameToDelete] = useState<string | null>(null);

    const { on, isConnected } = useSocket();

    useEffect(() => {
        const cleanupAccepted = on('challenge_accepted', (data: { gameId: string }) => {
            console.log('Tu reto fue aceptado! Redirigiendo a la partida:', data.gameId);
            navigate(`/play?id=${data.gameId}&online=true`);
        });

        const cleanupNew = on('new_challenge', (challenge: any) => {
            console.log('[Dashboard] NUEVO reto detectado via WebSocket:', challenge);
            setChallenges(prev => {
                if (prev.find(c => c.id === challenge.id)) return prev;
                return [challenge, ...prev];
            });
        });

        return () => {
            cleanupAccepted();
            cleanupNew();
        };
    }, [on, navigate]);

    // Polling fallback: every 2 seconds check if our open challenges got accepted
    useEffect(() => {
        const myOpenChallengeIds = challenges
            .filter(c => c.creatorId === user?.id && c.status === 'open')
            .map(c => c.id);

        if (myOpenChallengeIds.length === 0) return;

        const interval = setInterval(async () => {
            try {
                const res = await api.get('/challenges/mine');
                const mine: any[] = res.data;
                const accepted = mine.find(c => c.status === 'accepted' && c.gameId);
                if (accepted) {
                    clearInterval(interval);
                    navigate(`/play?id=${accepted.gameId}&online=true`);
                }
            } catch {/* silent */ }
        }, 2000);

        return () => clearInterval(interval);
    }, [challenges, user?.id, navigate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [gamesRes, challengesRes] = await Promise.all([
                    api.get('/games'),
                    api.get('/challenges')
                ]);
                setGames(gamesRes.data);
                setChallenges(challengesRes.data);

                // Auto-join challenge if ID is in URL
                const params = new URLSearchParams(window.location.search);
                const challengeId = params.get('challenge');
                if (challengeId) {
                    try {
                        const chRes = await api.get(`/challenges/${challengeId}`);
                        const targetChallenge = chRes.data;
                        if (targetChallenge && targetChallenge.creatorId !== user?.id && targetChallenge.status === 'open') {
                            const acceptRes = await api.post(`/challenges/${challengeId}/accept`);
                            navigate(`/play?id=${acceptRes.data.gameId}&online=true`);
                        }
                    } catch (e) {
                        console.error('Error auto-accepting challenge from URL', e);
                        navigate('/dashboard', { replace: true });
                    }
                }
            } catch (error) {
                console.error('Error al cargar datos:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user, navigate]);

    const TIME_OPTIONS = [
        { label: '1 min', value: '1+0' },
        { label: '1+1', value: '1+1' },
        { label: '3 min', value: '3+0' },
        { label: '3+2', value: '3+2' },
        { label: '5 min', value: '5+0' },
        { label: '10 min', value: '10+0' },
        { label: '10+5', value: '10+5' },
        { label: '30 min', value: '30+0' },
        { label: '1 hora', value: '60+0' },
        { label: 'Correspondencia (24h)', value: '1440+0' },
        { label: 'Sin Tiempo', value: 'unlimited' }
    ];

    const [selectedTC, setSelectedTC] = useState('10+0');
    const [selectedColor, setSelectedColor] = useState<string | null>(null);

    const createChallenge = async () => {
        if (isCreating) return;
        setIsCreating(true);
        try {
            const res = await api.post('/challenges', {
                timeControl: selectedTC,
                colorPreference: selectedColor
            });
            setChallenges(prev => [res.data, ...prev]);
        } catch (error: any) {
            console.error('Error al crear reto:', error);
            alert('No se pudo crear el reto.');
        } finally {
            setIsCreating(false);
        }
    };

    const acceptChallenge = async (id: string) => {
        try {
            const res = await api.post(`/challenges/${id}/accept`);
            navigate(`/play?id=${res.data.gameId}&online=true`);
        } catch (error) {
            console.error('Error al aceptar reto:', error);
        }
    };

    const cancelChallenge = async (id: string) => {
        try {
            await api.delete(`/challenges/${id}`);
            setChallenges(prev => prev.filter(c => c.id !== id));
        } catch (error) {
            console.error('Error al cancelar reto:', error);
        }
    };

    const confirmDeleteGame = async () => {
        if (!gameToDelete) return;
        try {
            await api.delete(`/games/${gameToDelete}`);
            setGames(prev => prev.filter(pg => pg.id !== gameToDelete));
        } catch (e) {
            console.error(e);
        } finally {
            setGameToDelete(null);
        }
    };

    const copyChallengeLink = (id: string) => {
        const url = `${window.location.origin}/dashboard?challenge=${id}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopySuccess(id);
            setTimeout(() => setCopySuccess(null), 2000);
        });
    };

    return (
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                    <Logo size="small" />
                    <div style={{ height: '32px', width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                    <div>
                        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>Hola, {user?.name || 'Jugador'}</h1>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Bienvenido de nuevo a ChessDrez</p>
                    </div>
                </div>
                <button
                    onClick={logout}
                    style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', padding: '0.5rem 1rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                >
                    <LogOut size={18} /> Cerrar sesión
                </button>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
                <aside>
                    <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Crear Nuevo Reto</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: isConnected ? '#10b981' : '#ef4444' }} />
                                    <span style={{ fontSize: '8px', fontWeight: 900, color: isConnected ? '#10b981' : '#ef4444' }}>{isConnected ? 'ONLINE' : 'OFFLINE'}</span>
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div>
                                    <label style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem', display: 'block' }}>RITMO DE JUEGO</label>
                                    <select 
                                        value={selectedTC} 
                                        onChange={(e) => setSelectedTC(e.target.value)}
                                        style={{ width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '0.6rem', borderRadius: '0.5rem', fontSize: '12px', outline: 'none' }}
                                    >
                                        {TIME_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div>
                                    <label style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem', display: 'block' }}>COLOR PREFERIDO</label>
                                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                                        {[
                                            { id: 'w', label: 'W', bg: '#fff', color: '#000' },
                                            { id: null, label: '?', bg: 'rgba(255,255,255,0.1)', color: '#fff' },
                                            { id: 'b', label: 'B', bg: '#000', color: '#fff' }
                                        ].map(opt => (
                                            <button 
                                                key={String(opt.id)}
                                                onClick={() => setSelectedColor(opt.id)}
                                                style={{ 
                                                    flex: 1, padding: '0.5rem', borderRadius: '0.4rem', border: selectedColor === opt.id ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)', 
                                                    background: opt.bg, color: opt.color, fontWeight: 900, cursor: 'pointer', fontSize: '10px', transition: 'all 0.2s'
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={createChallenge}
                            className="btn-primary"
                            disabled={isCreating || !isConnected}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.75rem',
                                border: 'none',
                                width: '100%',
                                cursor: (isCreating || !isConnected) ? 'not-allowed' : 'pointer',
                                marginBottom: '1rem',
                                opacity: (isCreating || !isConnected) ? 0.7 : 1,
                                height: '50px'
                            }}
                        >
                            <Plus size={20} /> {isCreating ? 'PUBLICANDO...' : 'PUBLICAR RETO'}
                        </button>
                        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <Link to="/play" style={{ textDecoration: 'none', color: 'var(--text-muted)', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <Play size={18} /> Jugar contra CPU
                            </Link>
                            <div style={{ padding: '0.75rem 1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', marginBottom: '0.5rem' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ELO Partida</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#3b82f6' }}>{user?.elo || 1200}</div>
                            </div>
                            <div style={{ padding: '0.75rem 1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rating Puzzles</div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f59e0b' }}>{user?.puzzleRating || 1200}</div>
                            </div>
                        </nav>
                    </div>
                </aside>

                <main>
                    {/* MIS RETOS */}
                    {challenges.filter(c => c.creatorId === user?.id).length > 0 && (
                        <>
                            <h2 style={{ fontSize: '1.2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#6366f1' }}>
                                <Swords size={20} /> Mis retos activos
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                                {challenges
                                    .filter(c => c.creatorId === user?.id)
                                    .map(c => (
                                        <div key={c.id} className="glass-card" style={{ padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(99, 102, 241, 0.3)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <div className="animate-pulse" style={{ fontSize: '0.8rem', color: '#6366f1', fontWeight: 700 }}>ESPERANDO OPONENTE...</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.timeControl}</div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                                <button onClick={() => copyChallengeLink(c.id)} style={{ background: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {copySuccess === c.id ? '¡COPIADO!' : 'COPIAR ENLACE'}
                                                </button>
                                                <button onClick={() => cancelChallenge(c.id)} style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
                                                    CANCELAR
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}

                    <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Swords size={24} /> Retos abiertos
                    </h2>
                    {challenges.filter(c => c.creatorId !== user?.id).length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', padding: '1rem 0' }}>No hay retos públicos de otros jugadores en este momento.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '3rem' }}>
                            {challenges
                                .filter(c => c.creatorId !== user?.id)
                                .map(c => (
                                    <div key={c.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                            <div style={{ width: '48px', height: '48px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                                                <Swords size={20} />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{c.creator?.name || 'Anon'} ({c.creator?.elo || 500})</div>
                                                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{c.timeControl || 'Ilimitado'} • {c.colorPreference === 'w' ? 'Blancas' : c.colorPreference === 'b' ? 'Negras' : 'Aleatorio'}</div>
                                            </div>
                                        </div>
                                        <button onClick={() => acceptChallenge(c.id)} className="btn-primary" style={{ border: 'none', cursor: 'pointer', padding: '0.5rem 1.5rem' }}>
                                            Aceptar
                                        </button>
                                    </div>
                                ))}
                        </div>
                    )}

                    <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Play size={24} /> Partidas en Curso
                    </h2>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>Cargando partidas...</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginBottom: '3rem' }}>
                            {games.filter(g => g.status === 'playing').length === 0 ? (
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No tienes partidas en curso en este momento.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {games.filter(g => g.status === 'playing').map(game => (
                                        <GameItem key={game.id} game={game} user={user} onDeleteClick={() => setGameToDelete(game.id)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <History size={24} /> Historial de Partidas
                    </h2>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>Cargando historial...</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                            {/* SECCIÓN PVP (Historial) */}
                            <div>
                                <h3 style={{ fontSize: '1rem', color: '#6366f1', marginBottom: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Partidas Online (PvP) Finalizadas</h3>
                                {games.filter(g => g.source === 'pvp' && g.status !== 'playing').length === 0 ? (
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No tienes partidas online finalizadas.</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        {games.filter(g => g.source === 'pvp' && g.status !== 'playing').map(game => (
                                            <GameItem key={game.id} game={game} user={user} onDeleteClick={() => setGameToDelete(game.id)} />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* SECCIÓN IA (Historial) */}
                            <div>
                                <h3 style={{ fontSize: '1rem', color: 'var(--primary)', marginBottom: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Entrenamiento vs IA Finalizadas</h3>
                                {games.filter(g => g.source !== 'pvp' && g.status !== 'playing').length === 0 ? (
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No tienes partidas finalizadas contra la IA.</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        {games.filter(g => g.source !== 'pvp' && g.status !== 'playing').map(game => (
                                            <GameItem key={game.id} game={game} user={user} onDeleteClick={() => setGameToDelete(game.id)} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>

            <ConfirmModal
                isOpen={!!gameToDelete}
                title="Borrar Partida"
                message="¿Estás seguro de que deseas borrar esta partida del historial? Esta acción no se puede deshacer."
                confirmText="Borrar"
                cancelText="Cancelar"
                onConfirm={confirmDeleteGame}
                onCancel={() => setGameToDelete(null)}
                type="danger"
            />
        </div>
    );
};

const GameItem = ({ game, user, onDeleteClick }: { game: any, user: any, onDeleteClick: () => void }) => {
    const isWhite = game.whitePlayerId === user?.id;
    const opponent = isWhite ? game.blackPlayer : game.whitePlayer;

    return (
        <div className="glass-card" style={{ padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                <div style={{ width: '40px', height: '40px', background: game.source === 'pvp' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {game.source === 'pvp' ? <Swords size={18} color="#6366f1" /> : <Play size={18} color="var(--primary)" />}
                </div>
                <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {game.source === 'pvp'
                            ? `vs ${opponent?.name || 'Oponente'}`
                            : `Contra CPU (Nivel ${game.aiLevel || '?'})`}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {new Date(game.createdAt).toLocaleDateString()} • {game.status === 'playing' ? <span style={{ color: '#10b981' }}>EN CURSO</span> : 'FINALIZADA'}
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Link to={`/play?id=${game.id}${game.source === 'pvp' ? '&online=true' : ''}`} className="btn-primary" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>
                    {game.status === 'playing' ? 'CONTINUAR' : 'VER'} <ChevronRight size={14} />
                </Link>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        onDeleteClick();
                    }}
                    style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                >
                    BORRAR
                </button>
            </div>
        </div>
    );
};

export default DashboardPage;
